/**
 * Shared types for the auth package.
 */

import type {
    postloginJoinStudentMutationClassroom as Classroom,
    postloginJoinStudentMutationError as MutationError,
} from "../graphql/join-classrooms-postlogin.ts";

export type JoinClassroomsError = MutationError;
export type JoinClassroomsStudentList = Classroom;

export interface JoinClassroomsData {
    errors: Array<JoinClassroomsError> | null | undefined;
    studentLists: Array<JoinClassroomsStudentList> | null | undefined;
}

export type LoginType =
    | "apple"
    | "facebook"
    | "google"
    | "clever"
    | "clever_district"
    | "clever_library"
    | "password"
    | "classlink"
    | "microsoft";

export interface LoginUserData {
    kaid: string;
}

/**
 * The third-party data that is passed in the mutation result to the client.
 */
export interface LoginTypeData {
    isFirstLogin: boolean;
    joinClassrooms?: JoinClassroomsData | undefined;
}

// Type of JSON returned from server after submitting sign up form.
export interface ResponseJSON {
    errors?: any;
    under13: boolean;
    continueUrl: string;
}

export type Purpose = "signup" | "login";
export type Referral =
    | "LearnStorm"
    // Original Khanmigo Checkout referral code used at launch. This referral
    // updates the UI to show Khanmigo branding, and the pricing for all
    // user types.
    | "KhanmigoCheckout"
    // KhanmigoSignup is the new referral code for all Khanmigo sign-ups
    // moving forward. With this referral code, the sign up page will indicate
    // learners and parents must pay, and teachers get free tools.
    // There is no longer a way for teachers to buy standard Khanmigo.
    | "KhanmigoSignup"
    | "parent_approval_success"
    | "parent_approval_failed"
    | "default";

// By listing the available roles separate from the Role type we can
// validate `unknown` types in type guards.
export const AVAILABLE_ROLES = ["learner", "teacher", "parent"] as const;
export type Role = (typeof AVAILABLE_ROLES)[number];

export type Step =
    | ""
    | "intro"
    | "confirmClass"
    | "identify"
    // TODO(davemcalpine): this probably should be "identify" to match the
    // existing step name, but it's been this way so long, I'd rather
    // investigate more before changing it.
    | "identity"
    | "signUp"
    | "login"
    | "reset";
export type UpdateStep = (step: Step) => void;

export type UpdateParentEmailStep = "initial" | "updateEmail" | "resendSuccess";

export interface UserClass {
    name: string;
    signupCode: string;
    includesCentrallyOwnedDistrictData: boolean;
}

// On the server learners are described as the absence of any user role.
// sync-start:user-role 1311316851 https://github.com/Khan/webapp/blob/master/services/users/schema.graphql
export type UserRole = "TEACHER" | "PARENT";
// sync-end:user-role

export type OAuthType = "GOOGLE" | "FACEBOOK" | "APPLE";

export interface UnverifiedUser {
    email: string;
    nickname: string | null | undefined;
    username: string | null | undefined;
}

// AuthType constants for the `context` prop, used to indicate
// whether the component is handling SIGNUP or LOGIN actions.
export const authType = {
    SIGNUP: "SIGNUP",
    LOGIN: "LOGIN",
    REAUTH: "REAUTH",
} as const;

export type AuthType = (typeof authType)[keyof typeof authType];
